import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import type {
  EnforcementEvent,
  NerEntity,
  RiskFragment,
  RiskTerm,
  TerminologyConstraint,
} from "@/api/types";

/**
 * Render plain text with mt-span overlays.
 *
 * Каждый span получает:
 *  - `kind` — visual style (constraint/risk-high/risk-attn/enforced/info)
 *  - `linkId` — стабильный ID для **cross-pane hover** (source ↔ target ↔ rail).
 *    Spans с одинаковым linkId подсвечиваются вместе. Стратегия:
 *      • constraint / enforced / accepted_variation → `c-<constraint_id>`
 *      • high_risk_term / attention_term            → `c-<constraint_id>` если есть, иначе `t-<surface>`
 *      • high_risk_fragment / quantity-regex        → `q-<digits-only-norm>` (общий для EN/RU)
 *      • abbreviation (regex или backend)           → `a-<canonical>` через EN↔RU mapping
 *      • lexicon_match                              → `l-<term-lower>` (только source-side, cross-pane обычно не имеет смысла)
 */

export type SpanKind =
  | "constraint"
  | "risk-high"
  | "risk-attn"
  | "issue"
  | "enforced"
  | "info"
  | "ner-entity";

export interface LexiconMatch {
  term: string;
  domain?: string;
  source?: string;
}

export interface AcceptedVariation {
  span?: { start: number; end: number };
  accepted_text?: string;
  matched_term?: string;
  target_text?: string;
  constraint_id?: string;
  match_type?: string;
}

/** EN ↔ RU mapping → canonical linkId. Hover на T2DM в source подсветит
 *  все «СД2» в target (и наоборот). */
const ABBR_LINK_MAP: Record<string, string> = {
  T2DM: "t2dm", СД2: "t2dm",
  T1DM: "t1dm", СД1: "t1dm",
  CV: "cv", СС: "cv", ССЗ: "cv",
  HF: "hf", СН: "hf",
  MI: "mi", ИМ: "mi",
  HTN: "htn", АГ: "htn",
  COPD: "copd", ХОБЛ: "copd",
  DM: "dm", СД: "dm",
  AD: "ad",
  AF: "af",
  DOAC: "doac",
  CRCL: "crcl", КК: "crcl",
  BP: "bp", АД: "bp",
  HR: "hr", ЧСС: "hr",
  RR: "rr", ЧДД: "rr",
  MAP: "map",
  STEMI: "stemi",
  NSTEMI: "nstemi",
  "SGLT-2": "sglt2", SGLT2: "sglt2",
  "GLP-1 RA": "glp1ra", "GLP-1 RAS": "glp1ra",
  "GLP1 RA": "glp1ra", "GLP1 RAS": "glp1ra",
  "GLP-1RA": "glp1ra", GLP1RA: "glp1ra",
  "ГПП-1": "glp1ra", "ГПП1": "glp1ra",
  ИБС: "ihd",
};

/** Нормализация substring для abbr lookup: trim, uppercase, нормализуем
 *  внутренние пробелы и дефисы. */
function normalizeAbbrToken(token: string): string {
  return token.trim().toUpperCase().replace(/[\s ]+/g, " ");
}

function abbrToLinkId(token: string): string {
  const norm = normalizeAbbrToken(token);
  if (ABBR_LINK_MAP[norm]) return "a-" + ABBR_LINK_MAP[norm];
  // Также пробуем без дефисов / без пробелов
  const compact = norm.replace(/[\s\-–—]+/g, "");
  if (ABBR_LINK_MAP[compact]) return "a-" + ABBR_LINK_MAP[compact];
  return "a-" + norm.toLowerCase().replace(/[^a-zа-я0-9]/giu, "");
}

/** Quantity regex — дозы/проценты/единицы. EN unit + RU unit оба ловятся;
 *  linkId на основе digits-only делает «90-95%» в source и «90–95 %» в target
 *  одинаковыми. */
const FALLBACK_QUANTITY_RE =
  /(?<![A-Za-zА-Яа-яЁё0-9])\d+(?:[.,]\d+)?(?:\s*[-–—]\s*\d+(?:[.,]\d+)?)?\s*(?:%|mg|mcg|kg|ml|iu|g|l|mmhg|мг|мкг|кг|г|мл|л|ме|мм\s*рт\.?\s*ст\.?)(?![A-Za-zА-Яа-яЁё0-9])/gi;

const UNIT_NORM: Record<string, string> = {
  mg: "mg", мг: "mg",
  mcg: "mcg", мкг: "mcg",
  kg: "kg", кг: "kg",
  g: "g", г: "g",
  ml: "ml", мл: "ml",
  l: "l", л: "l",
  iu: "iu", ме: "iu",
  mmhg: "mmhg",
  "%": "pct",
};

export function quantityToLinkId(token: string): string {
  // 1. lowercase
  // 2. убираем все whitespace и nbsp
  // 3. заменяем 5 видов dash'ей на "-"
  // 4. оставляем число + unit; unit нормализуем по UNIT_NORM
  const lc = token.toLowerCase().replace(/[\s  ]+/g, "").replace(/[–—]/g, "-");
  // вытащим число (или диапазон) и unit
  const m = lc.match(
    /^(\d+(?:[.,]\d+)?(?:-\d+(?:[.,]\d+)?)?)\s*(.+)$/,
  );
  if (!m) return "q-" + lc;
  const numbers = m[1].replace(/,/g, ".");
  const unit = m[2].replace(/[.\s]/g, "").replace(/^мм?ртст$/, "mmhg");
  const canonicalUnit = UNIT_NORM[unit] ?? unit;
  return `q-${numbers}-${canonicalUnit}`;
}

export type HighlightPane = "source" | "target";

export interface SpanEvent {
  start: number;
  end: number;
  kind: SpanKind;
  label?: string;
  /** Force-active class flag — set externally based on activeLinkIds. */
  active?: boolean;
  /** Stable cross-pane ID. Spans with the same linkId light up together
   *  on hover. */
  linkId?: string;
}

export interface HighlightInputs {
  pane?: HighlightPane;
  constraints?: TerminologyConstraint[];
  highRiskTerms?: RiskTerm[];
  highRiskFragments?: RiskFragment[];
  attentionTerms?: RiskTerm[];
  enforcementEvents?: EnforcementEvent[];
  lexiconMatches?: LexiconMatch[];
  acceptedVariations?: AcceptedVariation[];
  /** NER-сущности из pretranslate snapshot.entities — drug/dose/person/
   *  section найденные natasha + regex'ами, поверх glossary constraints. */
  entities?: NerEntity[];
  highlightAbbreviations?: boolean;
  /** Set of linkIds that should render with `is-active` class. */
  activeLinkIds?: ReadonlySet<string>;
}

function findOccurrences(
  text: string,
  needle: string,
): Array<{ start: number; end: number }> {
  if (!needle) return [];
  const out: Array<{ start: number; end: number }> = [];
  let from = 0;
  while (from < text.length) {
    const idx = text.indexOf(needle, from);
    if (idx < 0) break;
    out.push({ start: idx, end: idx + needle.length });
    from = idx + Math.max(1, needle.length);
  }
  return out;
}

function findOccurrencesCi(
  text: string,
  needle: string,
): Array<{ start: number; end: number }> {
  if (!needle) return [];
  const out: Array<{ start: number; end: number }> = [];
  const lower = text.toLowerCase();
  const lneedle = needle.toLowerCase();
  let from = 0;
  while (from < lower.length) {
    const idx = lower.indexOf(lneedle, from);
    if (idx < 0) break;
    out.push({ start: idx, end: idx + needle.length });
    from = idx + Math.max(1, needle.length);
  }
  return out;
}

/** Многострочный label для popover'а на constraint-span'е.
 *
 *  Native HTML `title=` поддерживает `\n` как перенос строки во всех
 *  основных браузерах, поэтому используем text-popover, не custom tooltip.
 *
 *  Формат для source pane:
 *  - Если есть source_text отдельно от surface (аббревиатура с раскрытием):
 *      ```
 *      ОНМК
 *      → острое нарушение мозгового кровообращения
 *      → acute cerebrovascular accident
 *      ⚠ другие значения:
 *         • cardiac decompensation
 *      ```
 *  - Иначе: `target_text` (как раньше).
 */
export function buildConstraintLabel(
  c: TerminologyConstraint,
  pane: HighlightPane,
): string {
  if (pane === "target") {
    // На target pane показываем source_text как контекст
    return c.source_text || c.target_text;
  }
  const lines: string[] = [];
  const surface = c.surface;
  const expansion = c.source_text;
  const target = c.target_text;
  // Если surface отличается от expansion — показываем оба
  if (surface && expansion && surface !== expansion) {
    lines.push(surface);
    lines.push("→ " + expansion);
    if (target) lines.push("→ " + target);
  } else if (target) {
    lines.push(target);
  } else if (expansion) {
    lines.push(expansion);
  }
  const alts = c.provenance?.alternatives ?? [];
  if (alts.length > 0) {
    lines.push("⚠ другие значения:");
    for (const alt of alts) {
      const t = alt.target_term;
      if (t) lines.push("   • " + t);
    }
  }
  return lines.join("\n");
}


export function buildSpanEvents(
  text: string,
  inputs: HighlightInputs,
): SpanEvent[] {
  const events: SpanEvent[] = [];
  const activeIds = inputs.activeLinkIds ?? new Set<string>();
  const pane: HighlightPane = inputs.pane ?? "source";

  const push = (e: SpanEvent): void => {
    if (e.linkId && activeIds.has(e.linkId)) {
      events.push({ ...e, active: true });
    } else {
      events.push(e);
    }
  };

  // 1. Constraints — explicit span (source) или substring fallback (any pane).
  for (const c of inputs.constraints ?? []) {
    const linkId = c.constraint_id ? "c-" + c.constraint_id : undefined;
    if (c.span && pane === "source") {
      const { start, end } = c.span;
      if (
        typeof start === "number" &&
        typeof end === "number" &&
        start >= 0 &&
        end > start &&
        end <= text.length
      ) {
        push({
          start,
          end,
          kind: "constraint",
          label: buildConstraintLabel(c, pane),
          linkId,
        });
        continue;
      }
    }
    const needle = pane === "source" ? c.source_text : c.target_text;
    if (!needle) continue;
    for (const occ of findOccurrencesCi(text, needle)) {
      push({
        start: occ.start,
        end: occ.end,
        kind: "constraint",
        label: buildConstraintLabel(c, pane),
        linkId,
      });
    }
  }

  // 2. Enforcement events (target pane only).
  if (pane === "target") {
    for (const e of inputs.enforcementEvents ?? []) {
      const needle = e.applied_text;
      if (!needle) continue;
      const linkId = e.constraint_id ? "c-" + e.constraint_id : undefined;
      for (const occ of findOccurrences(text, needle)) {
        push({
          start: occ.start,
          end: occ.end,
          kind: "enforced",
          label: e.action ? `${e.action}: ${e.matched_alias ?? ""}` : "enforced",
          linkId,
        });
      }
    }
  }

  // 3. Risk fragments — explicit span или substring; linkId по digit-norm.
  for (const f of inputs.highRiskFragments ?? []) {
    const linkId = "q-" + quantityToLinkIdFromFragment(f.fragment);
    if (
      pane === "source" &&
      f.span &&
      typeof f.span.start === "number" &&
      typeof f.span.end === "number" &&
      f.span.start >= 0 &&
      f.span.end > f.span.start &&
      f.span.end <= text.length
    ) {
      push({
        start: f.span.start,
        end: f.span.end,
        kind: "risk-high",
        label: f.description,
        linkId,
      });
      continue;
    }
    for (const occ of findOccurrences(text, f.fragment)) {
      push({
        start: occ.start,
        end: occ.end,
        kind: "risk-high",
        label: f.description,
        linkId,
      });
    }
  }

  // 4. Risk terms (high + attention).
  const pushRiskTerm = (
    t: RiskTerm,
    kind: "risk-high" | "risk-attn",
  ): void => {
    // Если term привязан к constraint — даём общий linkId с constraint.
    const constraintId = (t as unknown as { constraint_id?: string })
      .constraint_id;
    const linkId = constraintId
      ? "c-" + constraintId
      : "t-" + t.surface.toLowerCase();
    if (
      pane === "source" &&
      t.span &&
      typeof t.span.start === "number" &&
      typeof t.span.end === "number" &&
      t.span.start >= 0 &&
      t.span.end > t.span.start &&
      t.span.end <= text.length
    ) {
      push({
        start: t.span.start,
        end: t.span.end,
        kind,
        label: t.reason_codes.join(", "),
        linkId,
      });
      return;
    }
    for (const occ of findOccurrencesCi(text, t.surface)) {
      push({
        start: occ.start,
        end: occ.end,
        kind,
        label: t.reason_codes.join(", "),
        linkId,
      });
    }
  };
  for (const t of inputs.highRiskTerms ?? []) pushRiskTerm(t, "risk-high");
  for (const t of inputs.attentionTerms ?? []) pushRiskTerm(t, "risk-attn");

  // 5. Lexicon matches — info, без cross-pane (term отличается между EN/RU).
  for (const lm of inputs.lexiconMatches ?? []) {
    if (!lm.term) continue;
    const linkId = "l-" + lm.term.toLowerCase();
    for (const occ of findOccurrencesCi(text, lm.term)) {
      push({
        start: occ.start,
        end: occ.end,
        kind: "info",
        label: lm.domain
          ? `${lm.term} · ${lm.domain}${lm.source ? " · " + lm.source : ""}`
          : lm.term,
        linkId,
      });
    }
  }

  // 6. Accepted variations (target only). LinkId — общий с constraint.
  if (pane === "target") {
    for (const av of inputs.acceptedVariations ?? []) {
      const linkId = av.constraint_id ? "c-" + av.constraint_id : undefined;
      if (
        av.span &&
        typeof av.span.start === "number" &&
        typeof av.span.end === "number" &&
        av.span.start >= 0 &&
        av.span.end > av.span.start &&
        av.span.end <= text.length
      ) {
        push({
          start: av.span.start,
          end: av.span.end,
          kind: "constraint",
          label: av.matched_term
            ? `${av.matched_term} ← ${av.accepted_text ?? ""} (${av.match_type ?? "variation"})`
            : av.accepted_text,
          linkId,
        });
      } else if (av.accepted_text) {
        for (const occ of findOccurrencesCi(text, av.accepted_text)) {
          push({
            start: occ.start,
            end: occ.end,
            kind: "constraint",
            label: av.matched_term ?? av.accepted_text,
            linkId,
          });
        }
      }
    }
  }

  // 6.5. NER entities (drug / dose / person / location / organization / section).
  // Source-pane only (это про подсветку SOURCE текста для контекста переводчика).
  if (pane === "source") {
    for (const ent of inputs.entities ?? []) {
      if (!ent.span) continue;
      const { start, end } = ent.span;
      if (
        typeof start !== "number" ||
        typeof end !== "number" ||
        start < 0 ||
        end <= start ||
        end > text.length
      ) {
        continue;
      }
      const note =
        ent.kind === "person"
          ? "PII"
          : ent.kind === "drug" && ent.source === "regex_drug"
          ? "не в глоссарии"
          : ent.source.startsWith("regex_")
          ? "regex"
          : "NER";
      push({
        start,
        end,
        kind: "ner-entity",
        label: `${ent.kind} · ${ent.text} · ${note}`,
        linkId: `n-${ent.kind}-${start}-${end}`,
      });
    }
  }

  // 7. Abbreviations — substring search по списку (длинные впереди).
  if (inputs.highlightAbbreviations) {
    const tokens = [
      "GLP-1 RAs", "GLP-1 RA", "GLP-1RAs", "GLP-1RA",
      "GLP1 RAs", "GLP1 RA", "GLP1RAs", "GLP1RA",
      "NSTEMI", "STEMI",
      "SGLT-2", "SGLT2",
      "T1DM", "T2DM", "DOAC", "CrCl", "COPD",
      "HTN", "MAP", "MI",
      "AF", "AD", "BP", "CV", "DM", "HF", "HR", "RR",
      "ГПП-1", "ГПП1",  // ГПП-1, ГПП1
      "ХОБЛ",                       // ХОБЛ
      "КК",                         // КК (CrCl — клиренс креатинина)
      "ССЗ",                             // ССЗ
      "ИБС",                             // ИБС
      "ЧСС",                             // ЧСС
      "ЧДД",                             // ЧДД
      "СД1", "СД2",                 // СД1, СД2
      "АГ",                                   // АГ
      "АД",                                   // АД
      "ИМ",                                   // ИМ
      "СН",                                   // СН
      "СС",                                   // СС
    ];
    const boundary = /[A-Za-zА-яЁё0-9]/;
    for (const token of tokens) {
      let from = 0;
      while (from < text.length) {
        const idx = text.indexOf(token, from);
        if (idx < 0) break;
        const end = idx + token.length;
        const before = idx > 0 ? text.charAt(idx - 1) : "";
        const after = end < text.length ? text.charAt(end) : "";
        const beforeBlocked = before && boundary.test(before);
        const afterBlocked = after && boundary.test(after);
        if (!beforeBlocked && !afterBlocked) {
          push({
            start: idx,
            end,
            kind: "risk-attn",
            label: `${token} · аббревиатура`,
            linkId: abbrToLinkId(token),
          });
        }
        from = end;
      }
    }
  }

  // 8. Quantity regex — high-risk; linkId по digit-norm.
  if (inputs.highlightAbbreviations) {
    for (const m of text.matchAll(FALLBACK_QUANTITY_RE)) {
      if (typeof m.index !== "number") continue;
      push({
        start: m.index,
        end: m.index + m[0].length,
        kind: "risk-high",
        label: `${m[0]} · доза/измерение`,
        linkId: quantityToLinkId(m[0]),
      });
    }
  }

  // Sort: by start asc, by kind priority, longer wins.
  const kindRank: Record<SpanKind, number> = {
    constraint: 0,
    enforced: 1,
    "risk-high": 2,
    "risk-attn": 3,
    issue: 4,
    "ner-entity": 4.5,
    info: 5,
  };
  events.sort(
    (a, b) =>
      a.start - b.start ||
      kindRank[a.kind] - kindRank[b.kind] ||
      b.end - a.end,
  );
  return events;
}

/** Helper для high_risk_fragment.fragment → digits-norm. */
function quantityToLinkIdFromFragment(fragment: string): string {
  return quantityToLinkId(fragment).replace(/^q-/, "");
}

function pruneOverlaps(events: SpanEvent[]): SpanEvent[] {
  const out: SpanEvent[] = [];
  let cursor = 0;
  for (const e of events) {
    if (e.start < cursor) continue;
    out.push(e);
    cursor = e.end;
  }
  return out;
}

/** F9: кастомный popover вместо native HTML title. Появляется на mouseenter
 *  span'а, исчезает на mouseleave с дебаунсом 150 мс (чтобы юзер успел
 *  довести курсор в popover). Рендерится через portal в document.body —
 *  не страдает от overflow:hidden родителей. */
function HoverSpan({
  className,
  label,
  linkId,
  kind,
  children,
}: {
  className: string;
  label: string | undefined;
  linkId: string | undefined;
  kind: SpanKind;
  children: ReactNode;
}) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  // Computes popover anchor point (top center of span) in viewport coords.
  useLayoutEffect(() => {
    if (!open || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setPos({
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX + rect.width / 2,
    });
  }, [open]);

  const onEnter = () => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpen(true);
  };
  const onLeave = () => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), 150);
  };

  useEffect(() => {
    return () => {
      if (closeTimer.current !== null) {
        window.clearTimeout(closeTimer.current);
      }
    };
  }, []);

  const hasLabel = typeof label === "string" && label.length > 0;

  return (
    <>
      <span
        ref={ref}
        className={className}
        // native title оставляем как a11y/keyboard-fallback — браузер
        // покажет его если custom popover закрыт.
        title={hasLabel ? label : undefined}
        data-mt-link={linkId || undefined}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
      >
        {children}
      </span>
      {open && hasLabel && typeof document !== "undefined"
        ? createPortal(
            <HighlightPopover
              top={pos.top}
              left={pos.left}
              kind={kind}
              label={label as string}
              onEnter={onEnter}
              onLeave={onLeave}
            />,
            document.body,
          )
        : null}
    </>
  );
}

const POPOVER_OFFSET_PX = 10;
const POPOVER_MAX_WIDTH_PX = 360;

function HighlightPopover({
  top,
  left,
  kind,
  label,
  onEnter,
  onLeave,
}: {
  top: number;
  left: number;
  kind: SpanKind;
  label: string;
  onEnter: () => void;
  onLeave: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shift, setShift] = useState({ dx: 0, dy: -POPOVER_OFFSET_PX });
  // Сдвигаем popover если упирается в viewport.
  useLayoutEffect(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    let dx = 0;
    let dy = -POPOVER_OFFSET_PX;
    // Слева
    if (rect.left < 8) dx = 8 - rect.left;
    // Справа
    if (rect.right > window.innerWidth - 8) {
      dx = window.innerWidth - 8 - rect.right;
    }
    // Сверху — если не помещается, перевернуть вниз
    if (rect.top < 8) {
      dy = rect.height + POPOVER_OFFSET_PX;
    }
    if (dx !== 0 || dy !== shift.dy) setShift({ dx, dy });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lines = label.split("\n");
  const head = lines[0] ?? "";
  const body = lines.slice(1);

  return (
    <div
      ref={ref}
      className={"mt-popover mt-popover--" + kind}
      role="tooltip"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      style={{
        position: "absolute",
        top,
        left,
        transform: `translate(calc(-50% + ${shift.dx}px), calc(-100% + ${shift.dy}px))`,
        maxWidth: POPOVER_MAX_WIDTH_PX,
        zIndex: 1000,
      }}
    >
      <div className="mt-popover__head">
        <span className="mt-popover__chip">{kindChip(kind)}</span>
        <span className="mt-popover__head-text">{head}</span>
      </div>
      {body.length > 0 && (
        <div className="mt-popover__body">
          {body.map((line, i) => (
            <div key={i} className="mt-popover__line">
              {line}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function kindChip(kind: SpanKind): string {
  switch (kind) {
    case "constraint":
      return "термин";
    case "risk-high":
      return "сигнал";
    case "risk-attn":
      return "внимание";
    case "enforced":
      return "правка";
    case "issue":
      return "ошибка";
    case "ner-entity":
      return "сущность";
    case "info":
    default:
      return "info";
  }
}

export function renderHighlighted(
  text: string,
  inputs: HighlightInputs,
): ReactNode[] {
  const events = pruneOverlaps(buildSpanEvents(text, inputs));
  if (events.length === 0) return [text];

  const out: ReactNode[] = [];
  let cursor = 0;
  events.forEach((e, idx) => {
    if (e.start > cursor) {
      out.push(text.slice(cursor, e.start));
    }
    const className =
      "mt-span mt-span--" + e.kind + (e.active ? " is-active" : "");
    out.push(
      <HoverSpan
        key={`s-${idx}`}
        className={className}
        label={e.label}
        linkId={e.linkId}
        kind={e.kind}
      >
        {text.slice(e.start, e.end)}
      </HoverSpan>,
    );
    cursor = e.end;
  });
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}
